// Pure formatting helpers extracted from SportsEventClient.tsx.
// Variant of the sports2 helpers — this version handles the full
// tennis score format ("1-6, 5-5", tiebreak "(7-5)", per-set arrays)
// and uses different rounding rules for volume display, so it is
// kept separate to preserve byte-identical output.

export function parseScore(raw: string): {
  home: string;
  away: string;
  seriesHome?: string;
  seriesAway?: string;
  format?: string;
  sets?: string[];
} {
  if (!raw) return { home: "", away: "" };
  // Esports: "000-000|1-0|Bo3" → strip leading zeros from map score
  const pipeParts = raw.split("|");
  if (pipeParts.length >= 2) {
    const mapParts = pipeParts[0].split("-");
    const seriesParts = pipeParts[1].split("-");
    return {
      home: String(parseInt(mapParts[0]?.trim()) || 0),
      away: String(parseInt(mapParts[1]?.trim()) || 0),
      seriesHome: seriesParts[0]?.trim(),
      seriesAway: seriesParts[1]?.trim(),
      format: pipeParts[2]?.trim(),
    };
  }
  // Tennis: "1-6, 5-5" or "7-6(7-5), 6-3" or "1-6, 6-6(3-2)"
  if (raw.includes(",")) {
    const sets = raw.split(",").map(s => s.trim());
    const currentSet = sets[sets.length - 1];
    // Extract tiebreak score if present: "6-6(3-2)" → tiebreak "3-2", game "6-6"
    const tbMatch = currentSet.match(/^(\d+)-(\d+)\((\d+)-(\d+)\)$/);
    let home: string, away: string;
    if (tbMatch) {
      // In tiebreak: show tiebreak score as main, game score in set display
      home = tbMatch[3];
      away = tbMatch[4];
    } else {
      const currentParts = currentSet.match(/^(\d+)-(\d+)/);
      home = currentParts?.[1] || "";
      away = currentParts?.[2] || "";
    }
    // Count sets won by each player
    let homeWon = 0, awayWon = 0;
    for (const set of sets.slice(0, -1)) {
      const m = set.match(/^(\d+)-(\d+)/);
      if (m) {
        if (parseInt(m[1]) > parseInt(m[2])) homeWon++;
        else awayWon++;
      }
    }
    // Clean set display: "6-6(3-2)" → "6-6 TB"
    const displaySets = sets.map(s => {
      const tb = s.match(/^(\d+-\d+)\(\d+-\d+\)$/);
      return tb ? `${tb[1]} TB` : s;
    });
    return {
      home,
      away,
      seriesHome: String(homeWon),
      seriesAway: String(awayWon),
      sets: displaySets,
    };
  }
  const parts = raw.split("-");
  return { home: parts[0]?.trim() || "", away: parts[1]?.trim() || "" };
}

export function formatPrice(price: number | null): string {
  if (price === null || price === undefined) return "-";
  // PM displays sub-cent decimals only on fine-tick markets (tennis,
  // some politics). Standard 0.01-tick markets land on whole cents and
  // PM shows them as integers — we mirror that by only showing a
  // decimal when the value drifts >0.05¢ from a whole cent.
  const cents = (price ?? 0) * 100;
  const rounded = Math.round(cents);
  const display = Math.abs(cents - rounded) > 0.05 ? cents.toFixed(1) : rounded.toString();
  return `${display}¢`;
}

export function formatVolume(vol: number | null | undefined): string {
  const n = Number(vol) || 0;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
}

/**
 * "Yasmine Kabbaj" → "Y. Kabbaj". Multi-part surnames preserved
 * ("Carlos Alcaraz Garfia" → "C. Alcaraz Garfia"); hyphenated first names
 * stay readable ("Jean-Luc Lopez" → "J-L. Lopez"). Mirrors the helper in
 * TennisHero so the trading modal matches the scoreboard.
 */
export function abbreviateTennisName(full: string): string {
  if (!full) return full;
  const parts = full.trim().split(/\s+/);
  if (parts.length < 2) return full;
  const first = parts[0];
  const surname = parts.slice(1).join(" ");
  const initials = first.split("-").map((p) => p.charAt(0).toUpperCase()).join("-");
  return `${initials}. ${surname}`;
}

/**
 * Humanize the terse period codes the backend passes through from
 * ESPN. Covers tennis sets, quarters, halves, innings, periods. Falls
 * through to the raw value for codes we don't know (better to show
 * "FOO" than nothing).
 */
export function humanizePeriod(raw: string | undefined, _league: string | undefined): string {
  if (!raw) return "";
  const p = raw.toUpperCase();
  // Tennis — sets
  const set = p.match(/^S(\d)$/);
  if (set) return `Set ${set[1]}`;
  // NBA/NFL quarters
  const q = p.match(/^Q(\d)$/);
  if (q) return `${q[1]}${["st", "nd", "rd", "th"][Math.min(parseInt(q[1]) - 1, 3)]} Q`;
  // NHL periods
  const period = p.match(/^P(\d)$/);
  if (period) return `${period[1]}${["st", "nd", "rd", "th"][Math.min(parseInt(period[1]) - 1, 3)]} P`;
  // Soccer halves
  if (p === "1H" || p === "H1") return "1st Half";
  if (p === "2H" || p === "H2") return "2nd Half";
  if (p === "HT") return "Halftime";
  if (p === "FT") return "Full Time";
  if (p === "ET") return "Extra Time";
  if (p === "PEN") return "Penalties";
  // Overtime / shootout
  if (p === "OT") return "OT";
  if (p === "SO") return "Shootout";
  // MLB innings — T1..T9 / B1..B9
  const inning = p.match(/^([TB])(\d+)$/);
  if (inning) {
    const half = inning[1] === "T" ? "Top" : "Bot";
    return `${half} ${inning[2]}`;
  }
  // Tiebreak
  if (p === "TB" || p === "TIEBREAK") return "Tiebreak";
  return raw;
}

export const LEAGUE_NAMES: Record<string, string> = {
  nba: "NBA",
  nhl: "NHL",
  mlb: "MLB",
  nfl: "NFL",
  mls: "MLS",
  wnba: "WNBA",
  epl: "Premier League",
  lal: "La Liga",
  bun: "Bundesliga",
  sea: "Serie A",
  fl1: "Ligue 1",
  ucl: "Champions League",
  uel: "Europa League",
  cbb: "NCAAB",
  cfb: "CFB",
  kbo: "KBO",
  cs2: "CS2",
  lol: "LoL",
  dota2: "Dota 2",
  val: "Valorant",
  mlbb: "MLBB",
  ow: "Overwatch",
  r6siege: "R6 Siege",
  ufc: "UFC",
  atp: "ATP",
  wta: "WTA",
  formula1: "Formula 1",
};
